import React from 'react';
import PropTypes, { func } from 'prop-types';
import {
  Body1Strong,
  Body1Stronger,
  mergeClasses,
} from '@fluentui/react-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import {
  FontsFamilySelector,
  FontsWeightSelector,
  fontFamilies,
} from '@bingads-webui-campaign-react/fonts-selector';

import { defaultFonts, overrideFontWeights } from './constants';
import { useFontsSelectorStyles } from './styles';

const defaultFont = fontFamilies[0].key;
const defaultFontWeight = overrideFontWeights[0].key;

const FontSelector = ({
  id,
  i18n,
  font,
  onChangeFont,
  onChangeWeight,
}) => {
  const classes = useFontsSelectorStyles();

  return (
    <div className={`${id}-font-selector`}>
      <div className={mergeClasses(`${id}-font-selector-label`, classes.fontSelectContainerHeader)}>
        <Body1Stronger as="h5" aria-label={i18n.getString(font.label)}>{i18n.getString(font.label)}</Body1Stronger>
        {font.helpTopic && (
          <div className={`${id}-font-selector-help`}>
            <HelpButton
              topic={font.helpTopic}
              aria-label={i18n.getString(font.helpLabel)}
            />
          </div>
        )}
      </div>
      <div className={classes.fontSelectContainer}>
        <FontsFamilySelector
          i18n={i18n}
          value={font.font}
          onChange={onChangeFont}
          className={classes.fontSelect}
          id={`${id}-font-selector-family`}
          ariaLabel={i18n.getString(_TL_('{{fontLabel}} font family'), { fontLabel: font.label })}
        />
        <FontsWeightSelector
          i18n={i18n}
          value={font.weight}
          onChange={onChangeWeight}
          id={`${id}-font-selector-weight`}
          overrideFontWeights={overrideFontWeights}
          selectClassName={classes.fontWeightSelect}
          ariaLabel={i18n.getString(_TL_('{{fontLabel}} font weight'), { fontLabel: font.label })}
        />
      </div>
    </div>
  );
};

FontSelector.propTypes = {
  id: PropTypes.string,
  i18n: PropTypes.shape({ getString: func }).isRequired,
  font: PropTypes.shape({
    label: PropTypes.string,
    font: PropTypes.string,
    weight: PropTypes.string,
    helpTopic: PropTypes.string,
    helpLabel: PropTypes.string,
  }),
  onChangeFont: PropTypes.func,
  onChangeWeight: PropTypes.func,
};

FontSelector.defaultProps = {
  id: '',
  font: {
    label: '',
    font: '',
    weight: '',
    helpTopic: '',
    helpLabel: '',
  },
  onChangeFont: () => {},
  onChangeWeight: () => {},
};

export const FontsSelector = ({
  i18n,
  fonts,
  onChangeFonts,
}) => {
  const classes = useFontsSelectorStyles();

  const fontsToRender = defaultFonts.map((font) => {
    const foundFont = fonts.find(f => f.TextAssetType === font.TextAssetType);
    return foundFont
      ? {
        ...foundFont,
        Label: font.Label,
        helpTopic: font.helpTopic,
        helpLabel: font.helpLabel,
      }
      : font;
  });

  const handleChangeFont = (TextAssetType, selectedFont, key) => {
    const updatedFonts = fontsToRender.map((font) => {
      if (font.TextAssetType === TextAssetType) {
        return {
          ...font,
          Typeface: key === 'Typeface' ? selectedFont : font.Typeface || defaultFont,
          Weight: key === 'Weight' ? selectedFont : font.Weight || defaultFontWeight,
        };
      }
      return font;
    }).filter(font => font.Typeface && font.Weight);
    onChangeFonts(updatedFonts);
  };

  return (
    <section
      aria-label={i18n.getString(_TL_('Fonts'))}
      className={mergeClasses(
        'ads-studio-brand-kit-fonts-selector',
        classes.sectionContainer
      )}
    >
      <Body1Strong as="h4" aria-label={i18n.getString(_TL_('Choose the fonts that are associated with your brand.'))} className="ads-studio-brand-kit-fonts-selector-description">
        {i18n.getString(_TL_('Choose the fonts that are associated with your brand.'))}
      </Body1Strong>
      <div className={classes.fontsContainer}>
        {fontsToRender.map(({
          Label,
          Weight,
          Typeface,
          helpTopic,
          helpLabel,
          TextAssetType,
        }) => (
          <FontSelector
            i18n={i18n}
            id={TextAssetType}
            key={TextAssetType}
            font={{
              label: Label,
              font: Typeface,
              weight: Weight,
              helpLabel,
              helpTopic,
            }}
            onChangeFont={font => handleChangeFont(TextAssetType, font, 'Typeface')}
            onChangeWeight={weight => handleChangeFont(TextAssetType, weight, 'Weight')}
          />
        ))}
      </div>
    </section>
  );
};

FontsSelector.propTypes = {
  fonts: PropTypes.shape(defaultFonts),
  onChangeFonts: PropTypes.func,
  i18n: PropTypes.shape({ getString: func }).isRequired,
};

FontsSelector.defaultProps = {
  fonts: defaultFonts,
  onChangeFonts: () => {},
};
